import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PageHeader from "./PageHeader.jsx";
import {
  fetchPatientsList,
  createPatient,
  deletePatientById,
} from "../features/clinic/clinicThunks.js";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Trash2, X } from "lucide-react";

import "../styles/patients/patients.css";

const Patients = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const MIN_CHARS = 2;
  const lastParamsRef = useRef({ q: "", procedure: "" });

  const { list, loading, error, pagination } = useSelector(
    (state) => state.clinic
  );
  const { user } = useSelector((state) => state.auth);
  const role = user?.role;

  const [q, setQ] = useState("");
  const [proc, setProc] = useState("");

  // --- Create modal ---
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // --- Delete modal ---
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState("");

  const [newPatient, setNewPatient] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const normalizeUsername = (text = "") =>
    text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, ".")
      .replace(/(^\.)|(\.$)/g, "")
      .slice(0, 30);

  const openCreate = useCallback(() => {
    setCreateError(null);
    setDeleteSuccess("");
    setNewPatient({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
    setCreateOpen(true);
  }, []);

  const closeCreate = useCallback(() => {
    if (creating) return;
    setCreateOpen(false);
    setCreateError(null);
    setNewPatient({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
    });
  }, [creating]);

  const submitCreate = useCallback(async () => {
    setCreateError(null);

    const firstName = String(newPatient.first_name || "").trim();
    const lastName = String(newPatient.last_name || "").trim();
    const email = String(newPatient.email || "").trim();
    const phone = String(newPatient.phone || "").trim();

    if (!firstName || !lastName || !email) {
      setCreateError("Nombre, apellidos y email son obligatorios.");
      return;
    }

    const emailLower = email.toLowerCase();
    const baseFromEmail = emailLower.includes("@")
      ? emailLower.split("@")[0] || ""
      : "";

    const baseFromName = `${firstName}.${lastName}`;
    const username = normalizeUsername(baseFromEmail || baseFromName);

    if (!username) {
      setCreateError("No se pudo generar el username automáticamente.");
      return;
    }

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      username,
    };

    setCreating(true);
    const result = await dispatch(createPatient(payload));
    setCreating(false);

    if (createPatient.fulfilled.match(result)) {
      const patientId = result.payload?.patient_id;
      closeCreate();

      const qTrim = (q || "").trim();
      dispatch(fetchPatientsList({ page: 1, q: qTrim, procedure: proc }));

      if (patientId) navigate(`/patients/${patientId}`);
      return;
    }

    setCreateError(result.payload || "No se pudo crear el paciente.");
  }, [dispatch, newPatient, q, proc, navigate, closeCreate]);

  const isStaff =
    user?.role === "doctor" ||
    user?.role === "receptionist" ||
    user?.role === "admin";
  const isPatient = role === "patient";

  useEffect(() => {
    if (!role) return;
    if (isStaff || isPatient) {
      dispatch(fetchPatientsList({ page: 1, q: "", procedure: "" }));
    }
  }, [dispatch, role, isStaff, isPatient]);

  useEffect(() => {
    if (!isStaff) return;

    const qTrim = (q || "").trim();
    const hasMinQ = qTrim.length >= MIN_CHARS;
    const hasProc = !!proc;

    const shouldFetch = hasMinQ || hasProc || (qTrim.length === 0 && !hasProc);
    if (!shouldFetch) return;

    const params = { q: qTrim, procedure: proc };
    const last = lastParamsRef.current;
    if (last.q === params.q && last.procedure === params.procedure) return;

    lastParamsRef.current = params;
    dispatch(fetchPatientsList({ page: 1, ...params }));
  }, [dispatch, isStaff, q, proc]);

  useEffect(() => {
    if (!isPatient) return;
    if (!list || list.length === 0) return;

    const myPatient = list[0];
    if (myPatient?.id) {
      navigate(`/patients/${myPatient.id}`, { replace: true });
    }
  }, [isPatient, list, navigate]);

  const openDelete = useCallback((patient) => {
    setCreateError(null);
    setDeleteError(null);
    setDeleteSuccess("");
    setPatientToDelete(patient);
    setDeleteOpen(true);
  }, []);

  const closeDelete = useCallback(() => {
    if (deleting) return;
    setDeleteOpen(false);
    setPatientToDelete(null);
    setDeleteError(null);
  }, [deleting]);

  const confirmDelete = useCallback(async () => {
    if (!patientToDelete?.id) return;

    setDeleteError(null);
    setDeleting(true);

    const result = await dispatch(deletePatientById(patientToDelete.id));

    setDeleting(false);

    if (deletePatientById.fulfilled.match(result)) {
      setDeleteOpen(false);
      setPatientToDelete(null);
      setDeleteSuccess("Paciente eliminado correctamente.");

      const page = pagination?.page || 1;
      const qTrim = (q || "").trim();
      dispatch(fetchPatientsList({ page, q: qTrim, procedure: proc }));
      return;
    }

    setDeleteError(result.payload || "No se pudo eliminar el paciente.");
  }, [dispatch, patientToDelete, pagination, q, proc]);

  useEffect(() => {
    const hasModal = createOpen || deleteOpen;
    if (!hasModal) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (deleteOpen) closeDelete();
      else if (createOpen) closeCreate();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [createOpen, deleteOpen, closeCreate, closeDelete]);

  const norm = (t = "") =>
    t.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const procedures = useMemo(() => {
    const s = new Set();
    (list || []).forEach((p) =>
      (p.surgeries || []).forEach(
        (x) => x.procedure_name && s.add(x.procedure_name)
      )
    );
    return [...s].sort();
  }, [list]);

  const filtered = useMemo(() => {
    const qn = norm(q);
    return (list || []).filter((p) => {
      const nameOk = !qn || norm(p.full_name || "").includes(qn);
      const procOk =
        !proc ||
        (p.surgeries || []).some((surgery) => surgery.procedure_name === proc);
      return nameOk && procOk;
    });
  }, [list, q, proc]);

  const latestSurgeryName = (p) => {
    const surgeries = (p.surgeries || [])
      .filter((x) => x?.date)
      .slice()
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return surgeries[0]?.procedure_name || "-";
  };

  const initials = (name = "") => {
    const [a = "", b = ""] = name.trim().split(/\s+/);
    return (a[0] || "").concat(b[0] || "").toUpperCase() || "?";
  };

  const fdate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return isNaN(d) ? "-" : d.toLocaleDateString();
  };

  const lastApptClass = (iso) => {
    if (!iso) return "tag neutral";
    const d = new Date(iso);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    if (d < today) return "tag dim";
    if (d.getTime() === today.getTime()) return "tag warning";
    return "tag";
  };

  // Paciente: no mostrar tabla
  if (isPatient) {
    return (
      <div className="patients-container">
        <div className="patients-top">
          <PageHeader title="Tu historial clínico" />
        </div>
        <div className="patients-empty">Cargando tu historial clínico…</div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="patients-container">
        <div className="patients-top">
          <PageHeader title="Pacientes" />
        </div>
        <div className="patients-empty">No autorizado.</div>
      </div>
    );
  }

  const goPage = (page) => {
    const qTrim = (q || "").trim();
    dispatch(fetchPatientsList({ page, q: qTrim, procedure: proc }));
  };

  const currentPage = pagination?.page || 1;
  const pageSize = 15;
  const total = pagination?.count || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showClear = !!(q || proc);

  return (
    <div className="patients-container">
      <div className="patients-top">
        <PageHeader title="Pacientes" />
      </div>

      <div className="patients-toolbar">
        <input
          className="patients-input"
          placeholder="Buscar por nombre..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <select
          className="patients-select"
          value={proc}
          onChange={(e) => setProc(e.target.value)}
        >
          <option value="">Filtrar por cirugía (todas)</option>
          {procedures.map((procedureName) => (
            <option key={procedureName} value={procedureName}>
              {procedureName}
            </option>
          ))}
        </select>

        <div className="patients-toolbar-actions">
          {showClear && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                setQ("");
                setProc("");
              }}
            >
              Limpiar
            </button>
          )}

          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={openCreate}
          >
            + Nuevo paciente
          </button>
        </div>
      </div>

      {deleteSuccess && (
        <div className="patients-banner patients-banner--success">
          {deleteSuccess}
        </div>
      )}
      {loading && <div className="patients-banner">Cargando…</div>}
      {error && (
        <div className="patients-banner patients-banner--error">
          Error: {error}
        </div>
      )}

      {filtered.length === 0 && !loading ? (
        <div className="patients-empty">No hay pacientes para mostrar.</div>
      ) : (
        <div>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th></th>
                  <th>Nombre</th>
                  <th>Cirugía</th>
                  <th>Última Cita</th>
                  <th className="th-actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.avatar_url ? (
                        <img
                          src={p.avatar_url}
                          alt={p.full_name}
                          className="avatar"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            if (e.currentTarget.nextSibling) {
                              e.currentTarget.nextSibling.style.display = "flex";
                            }
                          }}
                        />
                      ) : null}
                      <div
                        className="avatar-placeholder"
                        style={{ display: p.avatar_url ? "none" : "flex" }}
                        aria-hidden="true"
                      >
                        {initials(p.full_name)}
                      </div>
                    </td>

                    <td className="td-name">
                      {p.full_name || `Paciente #${p.id}`}
                    </td>
                    <td className="td-proc">{latestSurgeryName(p)}</td>

                    <td className="td-last">
                      <span className={lastApptClass(p.last_appointment)}>
                        {fdate(p.last_appointment)}
                      </span>
                    </td>

                    <td className="td-actions">
                      <div className="patient-actions">
                        <Link
                          to={`/patients/${p.id}`}
                          className="patient-action-icon"
                          aria-label="Ver / Editar"
                          title="Ver / Editar"
                        >
                          <Eye width={16} height={16} aria-hidden="true" />
                        </Link>

                        <button
                          type="button"
                          className="patient-action-icon patient-action-icon--danger"
                          onClick={() => openDelete(p)}
                          aria-label="Eliminar paciente"
                          title="Eliminar paciente"
                        >
                          <Trash2 width={16} height={16} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              type="button"
              className="ghost btn-sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => goPage(currentPage - 1)}
            >
              ← Anterior
            </button>

            <span className="pagination-label">
              Página <strong>{currentPage}</strong> de{" "}
              <strong>{totalPages}</strong> · {total} pacientes
            </span>

            <button
              type="button"
              className="ghost btn-sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => goPage(currentPage + 1)}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* MODAL CREAR */}
      {createOpen && (
        <div
          className="ui-modal-overlay"
          onClick={closeCreate}
          role="presentation"
        >
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Crear nuevo paciente"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Crear nuevo paciente</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeCreate}
                disabled={creating}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitCreate();
              }}
            >
              <div className="ui-modal-body patients-create-modal-body">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="patientFirstName">
                    Nombre
                  </label>
                  <input
                    id="patientFirstName"
                    className="ui-input"
                    placeholder="Nombre"
                    value={newPatient.first_name}
                    onChange={(e) =>
                      setNewPatient((p) => ({
                        ...p,
                        first_name: e.target.value,
                      }))
                    }
                    disabled={creating}
                    autoFocus
                  />
                </div>

                <div className="ui-field">
                  <label className="ui-label" htmlFor="patientLastName">
                    Apellidos
                  </label>
                  <input
                    id="patientLastName"
                    className="ui-input"
                    placeholder="Apellidos"
                    value={newPatient.last_name}
                    onChange={(e) =>
                      setNewPatient((p) => ({ ...p, last_name: e.target.value }))
                    }
                    disabled={creating}
                  />
                </div>

                <div className="ui-field patients-create-full">
                  <label className="ui-label" htmlFor="patientEmail">
                    Email *
                  </label>
                  <input
                    id="patientEmail"
                    className="ui-input"
                    type="email"
                    placeholder="Email"
                    value={newPatient.email}
                    onChange={(e) =>
                      setNewPatient((p) => ({ ...p, email: e.target.value }))
                    }
                    disabled={creating}
                  />
                </div>

                <div className="ui-field patients-create-full">
                  <label className="ui-label" htmlFor="patientPhone">
                    Teléfono (opcional)
                  </label>
                  <input
                    id="patientPhone"
                    className="ui-input"
                    placeholder="Teléfono"
                    value={newPatient.phone}
                    onChange={(e) =>
                      setNewPatient((p) => ({ ...p, phone: e.target.value }))
                    }
                    disabled={creating}
                  />
                </div>

                {createError ? (
                  <div className="ui-modal-error patients-create-full">
                    {createError}
                  </div>
                ) : null}
              </div>

              <div className="ui-modal-footer">
                <button
                  type="button"
                  className="ghost btn-sm"
                  onClick={closeCreate}
                  disabled={creating}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary btn-sm"
                  disabled={creating}
                >
                  {creating ? "Creando…" : "Crear paciente"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR */}
      {deleteOpen && (
        <div
          className="ui-modal-overlay"
          onClick={closeDelete}
          role="presentation"
        >
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar</h3>

              <button
                type="button"
                className="ghost btn-xs"
                onClick={closeDelete}
                disabled={deleting}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X width={16} height={16} aria-hidden="true" />
              </button>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar a{" "}
                <strong>
                  {patientToDelete?.full_name ||
                    `Paciente #${patientToDelete?.id}`}
                </strong>
                ?
                <div className="ui-delete-subhelp">
                  Esta acción desactiva el acceso del usuario y deja el paciente
                  como inactivo.
                </div>
              </div>

              {deleteError ? (
                <div className="ui-modal-error">{deleteError}</div>
              ) : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;